import { biToast } from '@pulsepointinc/bi-components';
import { useQueries } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getStatus } from '../lib/api/tasks';
import type { GetTaskStatusErrorResponse, GetTaskStatusResponse } from '../lib/api/tasks/dto';

const isErrorResponse = (r: GetTaskStatusResponse): r is GetTaskStatusErrorResponse => 'error' in r;

const POLL_INTERVAL = 4000;
const MAX_POLL_ATTEMPTS = 30; // 30 × 4s = 2 minutes

const pollCountMap = new Map<string, number>();

interface BaseTask {
  taskId: string;
  eventId?: string;
}

interface UseTaskPollingOptions<TTask extends BaseTask> {
  activeTasks: TTask[];
  removeTask: (taskId: string) => void;
  queryKeyPrefix: string;
  timeoutMessage: string;
  onComplete: (data: GetTaskStatusResponse, task: TTask, removeTask: (id: string) => void) => void | Promise<void>;
}

export const useTaskPolling = <TTask extends BaseTask>(options: UseTaskPollingOptions<TTask>): void => {
  const { activeTasks, removeTask, queryKeyPrefix, timeoutMessage, onComplete } = options;

  const createQueryConfig = (task: TTask) => ({
    queryKey: [queryKeyPrefix, task.taskId],
    queryFn: async () => {
      const response = await getStatus({ taskID: task.taskId, eventID: task.eventId });
      pollCountMap.set(task.taskId, (pollCountMap.get(task.taskId) || 0) + 1);
      return response;
    },
    enabled: !!task.taskId,
    refetchInterval: (query: { state: { data: GetTaskStatusResponse | undefined } }) => {
      const data = query.state.data;
      if (!data) return false;
      if (isErrorResponse(data)) {
        pollCountMap.delete(task.taskId);
        return false;
      }
      const isComplete = data.taskDetails.status === 'completed' || data.taskDetails.status === 'failed';
      if (isComplete) {
        pollCountMap.delete(task.taskId);
        return false;
      }
      const count = pollCountMap.get(task.taskId) || 0;
      if (count >= MAX_POLL_ATTEMPTS) {
        biToast.error(timeoutMessage);
        removeTask(task.taskId);
        pollCountMap.delete(task.taskId);
        return false;
      }
      return POLL_INTERVAL;
    },
    refetchIntervalInBackground: true,
    retry: 2,
  });

  const queries = useQueries({ queries: activeTasks.map(createQueryConfig) });

  useEffect(() => {
    activeTasks.forEach((task, index) => {
      const query = queries[index];
      if (query.error || query.isError) {
        removeTask(task.taskId);
        return;
      }
      if (!query.data) return;
      onComplete(query.data, task, removeTask);
    });
  }, [queries, activeTasks, removeTask, onComplete]);
};
